import { Link } from 'react-router-dom';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Pillar = { icon: SvgIconComponent; title: string; body: string; linkTo?: string };

const pillars: Pillar[] = [
  {
    icon: SecurityOutlinedIcon,
    title: 'Tenant isolation enforced at the database',
    body: 'PostgreSQL row-level security, keyed to a session-level tenant identifier the database itself enforces. A query that forgot to filter by tenant still cannot return another tenant\'s rows — Postgres refuses at the storage layer, not the application layer.',
    linkTo: '/blog/tenant-isolation-is-a-database-guarantee',
  },
  {
    icon: VerifiedUserOutlinedIcon,
    title: 'Every remote job is cryptographically signed',
    body: 'Ed25519-signed jobs against a default-deny capability registry. A device won\'t execute a script, patch instruction, or remote-control request it can\'t cryptographically verify came from the platform.',
    linkTo: '/blog/signed-jobs-cryptographic-endpoint-authorization',
  },
  {
    icon: WifiOffOutlinedIcon,
    title: 'Outbound-only monitoring agent',
    body: 'The on-prem agent never listens for anything at any client site — no port to open, no inbound firewall rule to add or justify at your next security review.',
    linkTo: '/blog/why-our-monitoring-agent-is-outbound-only',
  },
  {
    icon: LockOutlinedIcon,
    title: 'Secrets encrypted, fail-closed',
    body: 'Integration credentials are encrypted at rest under a dedicated application secret key. Production is configured to refuse to run misconfigured rather than silently store a secret in plaintext.',
  },
  {
    icon: RuleOutlinedIcon,
    title: 'CI gates every merge',
    body: 'Classification checks, a dependency audit, secret-scanning, and the full automated test suite all have to pass before any change reaches the production branch — no exceptions for a fast-moving week.',
  },
  {
    icon: ScienceOutlinedIcon,
    title: 'A published test-to-production ratio',
    body: 'We put the real number — computed automatically from the codebase, not asserted — on our own build status instead of asking you to trust the word "tested."',
    linkTo: '/blog/why-we-publish-our-test-to-production-ratio',
  },
];

export function Security(): JSX.Element {
  useSeo({
    title: 'Trust & security',
    description:
      'How Nexus is actually built: database-enforced tenant isolation, Ed25519-signed remote execution, an outbound-only monitoring agent, encrypted fail-closed secrets, and a CI pipeline that gates every merge.',
    path: '/security',
  });

  return (
    <div>
      <PageHero
        eyebrow="Trust & security"
        title="Platform assurance, not adjectives."
        subtitle="Any vendor can say 'secure' and 'tested.' Here's the specific, checkable architecture behind those words at Nexus — most of it verifiable straight from our own public repository, not a claim you have to take on faith."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              const card = (
                <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                    <Icon fontSize="small" />
                  </div>
                  <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-white/65">{p.body}</p>
                </div>
              );
              return (
                <Reveal key={p.title} delay={(i % 3) * 80}>
                  {p.linkTo ? (
                    <Link to={p.linkTo} className="block no-underline">
                      {card}
                    </Link>
                  ) : (
                    card
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.015] py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">Data ownership</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              For Microsoft 365 backup specifically, Nexus writes to an S3-compatible bucket you own
              (Wasabi, AWS S3, or Cloudflare R2) — not ours. Every object is hashed and verify-checked,
              and your data was never siloed inside Nexus-owned storage to begin with, so it can&apos;t
              become a hostage to a dispute or an outage on our end.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              See{' '}
              <Link to="/blog/your-bucket-your-keys-m365-backup" className="text-nexus-purple-light no-underline hover:underline">
                &quot;your bucket, your keys&quot;
              </Link>{' '}
              for the full architecture, and the{' '}
              <Link to="/compliance" className="text-nexus-purple-light no-underline hover:underline">
                compliance pages
              </Link>{' '}
              for how posture tracking maps to the specific framework your clients answer to.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                <MarkEmailReadOutlinedIcon fontSize="small" />
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-nexus-ink">Report a vulnerability</h2>
                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  We don&apos;t have a formal bug-bounty program yet — we&apos;re a small team and would
                  rather say that than pretend otherwise. What we do have: every report sent to{' '}
                  <a href="mailto:hello@nexusrmm.ai" className="text-nexus-purple-light no-underline hover:underline">
                    hello@nexusrmm.ai
                  </a>{' '}
                  (subject line: &quot;Security&quot;) reaches the team directly, and we read and respond
                  to every one.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-5 flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                <ReportProblemOutlinedIcon fontSize="small" />
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-nexus-ink">Incident disclosure</h2>
                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  Internally, every significant incident gets a same-day postmortem and a logged
                  evidence trail as a matter of discipline, not a retro nobody writes. If an incident
                  on our infrastructure ever affects a design partner, the commitment is the same
                  standard we hold the rest of this page to: told plainly, and on the timeline the
                  incident actually warrants — not delayed for a better news cycle.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Ask us to show you the architecture directly.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Design partners get a real walkthrough of the isolation and signing model — not a
                slide deck.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
